import {
  BaseSyntheticEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import useOrderDetails from './useOrderDetails';
import { NullableNumber } from '@app/common/types';
import { useForm } from 'react-hook-form';
import { FormData, TestCategory, TestType } from './types';
import useLoadOrderDetailValues from './useOrderDetailsValues';
import Loader from '@app/common/loadData/Loader';
import EditOrder from './editOrder';
import useSaveOrder from './useSaveOrder';
import Lang from '@lang/en';
import getRegisteredFieldsForEditOrder from './getRegisteredFieldsForEditOrder';
import { ValueType } from 'react-select';
import { SelectOption } from '@app/common/types';

type EditOrderFormI = {
  handleClose: () => void;
  id: NullableNumber;
  setShouldReload: (value: boolean) => void;
};

const EditOrderFormOrder = ({
  id,
  handleClose,
  setShouldReload,
}: EditOrderFormI): ReactElement => {
  const { orderDetails, loading } = useOrderDetails(id);

  const {
    register,
    reset,
    handleSubmit,
    formState,
    getValues,
    setValue,
  } = useForm<FormData>();
  const currentValues = getValues();

  const orderRegisteredFields = getRegisteredFieldsForEditOrder(register);
  const { loading: saveLoading, saveOrder } = useSaveOrder(orderDetails);
  const { errors, isSubmitSuccessful } = formState;
  useLoadOrderDetailValues(reset, orderDetails);

  const testTypes: Array<SelectOption> = [
    {
      value: 'antigen day_2',
      label: Lang.healthCheckAntigenTestLabelD2,
    },
    {
      value: 'antigen fit_to_fly_self',
      label: Lang.healthCheckAntigenTestLabelSelfFTF,
    },
    {
      value: 'antigen fit_to_fly_clinician',
      label: Lang.healthCheckAntigenTestLabelClinicianFTF,
    },
    {
      value: 'rt_pcr day_2',
      label: Lang.healthCheckPCRTestLabelD2,
    },
    {
      value: 'rt_pcr day_5',
      label: Lang.healthCheckPCRTestLabelD5,
    },
    {
      value: 'rt_pcr day_8',
      label: Lang.healthCheckPCRTestLabelD8,
    },
    {
      value: 'rt_pcr fit_to_fly_self',
      label: Lang.healthCheckPCRTestLabelSelfFTF,
    },
    {
      value: 'rt_pcr fit_to_fly_clinician',
      label: Lang.healthCheckPCRTestLabelClinicianFTF,
    },
  ];

  const handleSaveOrderInformation = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    handleSubmit(saveOrder)(event);
  };

  const onClose = useCallback(
    (event) => {
      event.preventDefault();
      handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (isSubmitSuccessful && handleClose) {
      setShouldReload(true);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const [testTypeSelectValue, setTestTypeSelectValue] = useState(
    testTypes[0].value
  );

  const handleChangeTestType = (
    event: ValueType<SelectOption, false>
  ): void => {
    if (event && typeof event.value === 'string') {
      const splitValue = event.value.split(' ');
      const type = splitValue[0] as TestType;
      const category = splitValue[1] as TestCategory;
      setValue('testType', type as TestType);
      setValue('testCategory', category as TestCategory);
      setTestTypeSelectValue(
        `${getValues('testType')} ${getValues('testCategory')}`
      );
    }
  };

  useEffect(() => {
    setTestTypeSelectValue(
      `${getValues('testType')} ${getValues('testCategory')}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValues]);

  const currentValueTestType = testTypes.filter((elem) => {
    if (elem.value === testTypeSelectValue) {
      return elem.value || testTypes[0].value;
    }
  })[0];

  return loading || saveLoading ? (
    <Loader />
  ) : (
    <form>
      <EditOrder
        orderRegisteredFields={orderRegisteredFields}
        data={orderDetails}
        handleClose={onClose}
        handleSaveOrderInformation={handleSaveOrderInformation}
        errors={errors}
        setValue={setValue}
        getValues={getValues}
        handleChangeTestType={handleChangeTestType}
        currentValueTestType={currentValueTestType}
        testTypes={testTypes}
      />
    </form>
  );
};

export default EditOrderFormOrder;
